import { useState, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import {
  ComposableMap, Geographies, Geography, ZoomableGroup,
} from 'react-simple-maps';
import { getISO3, isGlobalSouthISO3 } from '../../../utils/countryMapping';
import { parseCountryList } from '../../../utils/dataProcessor/parsers';
import { fetchTopoJSON, getTopoCache, geoToISO3 } from '../shared/constants';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/**
 * Lighten a hex colour by mixing it toward white.
 * amount 0 = original, 1 = white.
 */
function lightenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * ImpactAreaWorldMap
 *
 * Choropleth world map showing geographic distribution of results for a
 * specific CGIAR Impact Area.  Countries are shaded from a light tint of
 * the IA brand colour (few results) to the full colour (most results).
 */
const ImpactAreaWorldMap = memo(function ImpactAreaWorldMap({ results, color, label }) {
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [topology, setTopology] = useState(getTopoCache());

  useEffect(() => {
    if (!topology) {
      fetchTopoJSON().then(setTopology);
    }
  }, [topology]);

  // Build country counts from the results array
  const { iso3ToCounts, highlightedISO3, maxCount, exportData } = useMemo(() => {
    const countryCounts = {};

    if (results && results.length > 0) {
      for (const r of results) {
        // results may have pre-parsed countriesParsed array, or raw countries string
        const countries = r.countriesParsed || (r.countries ? parseCountryList(r.countries) : []);
        for (const c of countries) {
          countryCounts[c] = (countryCounts[c] || 0) + 1;
        }
      }
    }

    const _iso3ToCounts = {};
    const _highlighted = new Set();
    for (const [name, count] of Object.entries(countryCounts)) {
      const iso3 = getISO3(name);
      if (iso3 && isGlobalSouthISO3(iso3)) {
        _highlighted.add(iso3);
        _iso3ToCounts[iso3] = (_iso3ToCounts[iso3] || 0) + count;
      }
    }

    const counts = Object.values(_iso3ToCounts);
    const _max = counts.length > 0 ? Math.max(...counts) : 1;

    const _exportData = Object.entries(countryCounts)
      .map(([country, count]) => ({ Country: country, 'Result Count': count }))
      .sort((a, b) => b['Result Count'] - a['Result Count']);

    return {
      iso3ToCounts: _iso3ToCounts,
      highlightedISO3: _highlighted,
      maxCount: _max,
      exportData: _exportData,
    };
  }, [results]);

  const getColor = (iso3) => {
    if (!highlightedISO3.has(iso3)) return '#E5E7EB';
    const count = iso3ToCounts[iso3] || 1;
    const t = maxCount > 1 ? (count - 1) / (maxCount - 1) : 0;
    // Interpolate from light tint (amount 0.75) to full IA colour (amount 0)
    return lightenColor(color, 0.75 * (1 - t));
  };

  const handleMouseEnter = (geo, evt) => {
    const iso3 = geoToISO3(geo);
    const name = geo.properties.NAME || geo.properties.name || iso3;
    const count = iso3ToCounts[iso3];
    if (highlightedISO3.has(iso3)) {
      setTooltipContent(`${name}: ${count} result${count !== 1 ? 's' : ''}`);
    } else {
      setTooltipContent(name);
    }
    setTooltipPos({ x: evt.clientX, y: evt.clientY });
  };

  const handleMouseLeave = () => { setTooltipContent(''); };
  const handleMouseMove = (evt) => { if (tooltipContent) setTooltipPos({ x: evt.clientX, y: evt.clientY }); };

  if (!results || results.length === 0) {
    return (
      <ChartWrapper title="Geographic Distribution">
        <NoChartData
          message="No data available"
          detail={`No results with country data found for ${label || 'this Impact Area'}.`}
        />
      </ChartWrapper>
    );
  }

  if (!topology) {
    return (
      <ChartWrapper title="Geographic Distribution">
        <div className="flex items-center justify-center aspect-[16/7]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin" style={{ borderColor: color, borderTopColor: 'transparent' }} />
            <p className="text-sm text-gray-500">Loading map data...</p>
          </div>
        </div>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper
      title="Geographic Distribution"
      info={`Countries where results tagged for ${label || 'this Impact Area'} were reported. Darker shading indicates more results.`}
      chartData={exportData}
      sourceResults={results}
      exportFilename={`impact-area-map-${(label || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="relative aspect-[16/7]" onMouseMove={handleMouseMove}>
        <ComposableMap
          projection="geoEquirectangular"
          projectionConfig={{ rotate: [-10, 0, 0], scale: 130 }}
          width={800}
          height={350}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup>
            <Geographies geography={topology}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const iso3 = geoToISO3(geo);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getColor(iso3)}
                      stroke="#fff"
                      strokeWidth={0.5}
                      onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        default: { outline: 'none' },
                        hover: {
                          fill: highlightedISO3.has(iso3) ? color : '#D1D5DB',
                          outline: 'none',
                          cursor: 'pointer',
                        },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        {tooltipContent && (
          <div
            className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none"
            style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 28 }}
          >
            {tooltipContent}
          </div>
        )}
      </div>
      {highlightedISO3.size > 0 && (
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
          <span>Fewer results</span>
          <div
            className="w-24 h-3 rounded"
            style={{ background: `linear-gradient(to right, ${lightenColor(color, 0.75)}, ${color})` }}
          />
          <span>More results</span>
        </div>
      )}
    </ChartWrapper>
  );
});

ImpactAreaWorldMap.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
  label: PropTypes.string,
};

ImpactAreaWorldMap.defaultProps = {
  label: 'Impact Area',
};

export default ImpactAreaWorldMap;
